import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { useConvexInvestments } from "../../../services/convexService";
import { Id } from "../../../../backend/_generated/dataModel";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { X } from "lucide-react";
import { useLanguage } from "../../../contexts/LanguageContext";
import CurrencyDisplay from "../../../components/currency/CurrencyDisplay";

interface InvestmentModalProps {
  projectId: Id<"projects">;
  minInvestment: number;
  maxInvestment: number;
  projectTitle: string;
  projectFundingGoal: number;
  userInvestmentCapacity: number | undefined;
  isOpen: boolean;
  onClose: () => void;
}

const InvestmentModal = ({
  projectId,
  minInvestment,
  maxInvestment,
  projectTitle,
  projectFundingGoal,
  userInvestmentCapacity,
  isOpen,
  onClose
}: InvestmentModalProps) => {
  const [investmentAmount, setInvestmentAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const { createInvestment } = useConvexInvestments();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const amount = parseFloat(investmentAmount);

    if (isNaN(amount)) {
      toast.error(t("investment.errors.invalidAmount"));
      return;
    }

    if (amount < minInvestment) {
      toast.error(`${t("investment.errors.minAmountText")} ${minInvestment}`);
      return;
    }

    // Calculate the maximum possible investment based on user's capacity and project's remaining funding goal
    const maxPossibleInvestment = Math.min(
      maxInvestment,
      userInvestmentCapacity || Infinity
    );

    if (amount > maxPossibleInvestment) {
      toast.error(`${t("investment.errors.maxAmountText")} ${maxPossibleInvestment}`);
      return;
    }

    setLoading(true);

    try {
      if (!user) {
        toast.error("You must be logged in to make an investment");
        return;
      }

      const investmentId = await createInvestment({
        projectId,
        investorId: user.id,
        amount,
      });

      if (investmentId) {
        toast.success(t("investment.success.pending"));
        onClose();
        // Redirect to investor dashboard after successful submission
        navigate("/dashboard/investor");
      } else {
        toast.error(t("investment.errors.submitFailed"));
      }
    } catch (error: any) {
      console.error("Error submitting investment:", error);
      const errorMessage = error.message || t("investment.errors.submitFailed") || "Failed to submit investment request. Please try again or contact support if the problem persists.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Calculate the maximum possible investment based on project's remaining funding and user's investment capacity
  const maxPossibleInvestment = Math.min(
    maxInvestment,
    userInvestmentCapacity || Infinity
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg w-full max-w-md">
        <div className="flex justify-between items-center border-b p-4">
          <h3 className="text-lg font-semibold">{t("investment.investIn")} {projectTitle}</h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="investmentAmount">{t("investment.amountLabel")}</Label>
              <Input
                id="investmentAmount"
                type="number"
                value={investmentAmount}
                onChange={(e) => setInvestmentAmount(e.target.value)}
                placeholder={`${t("investment.amountPlaceholderMin")} ${minInvestment})`}
                min={minInvestment}
                max={maxPossibleInvestment}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t("investment.minMaxInfoMin")} <CurrencyDisplay amount={minInvestment} />, {t("investment.minMaxInfoMax")} <CurrencyDisplay amount={maxPossibleInvestment} />
              </p>
            </div>

            <div className="space-y-2 text-sm">
              <p className="text-muted-foreground">
                <span className="font-medium">{t("investment.projectFundingGoal")}:</span> <CurrencyDisplay amount={projectFundingGoal} />
              </p>
              <p className="text-muted-foreground">
                <span className="font-medium">{t("investment.remainingToGoal")}:</span> <CurrencyDisplay amount={maxInvestment} />
              </p>
              {userInvestmentCapacity && (
                <p className="text-muted-foreground">
                  <span className="font-medium">{t("investment.yourCapacity")}:</span> <CurrencyDisplay amount={userInvestmentCapacity} />
                </p>
              )}
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-800 mb-2">{t("investment.processTitle")}</h4>
              <ul className="text-sm text-yellow-700 list-disc pl-5 space-y-1">
                <li>{t("investment.processStep1")}</li>
                <li>{t("investment.processStep2")}</li>
                <li>{t("investment.processStep3")}</li>
                <li>{t("investment.processStep4")}</li>
              </ul>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" className="flex-1" disabled={loading}>
                {loading ? t("investment.submitting") : t("investment.submitRequest")}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InvestmentModal;